/*
 * SVG recreation of the Lowkey mobile app, ported from the App Store asset kit in lowkeyclient
 * (store-assets/src/kit). Sizes are iPhone points (393 x 852 screen), colors and copy formats follow the app's
 * Colors.js, RoutineAnimatedGrayButton, FeedHeader, BuildingSwitcher, and ManageInvitedUsersScreen, so the
 * marketing pages stay in step with the shipped app without screenshots.
 */
import React, { useId } from 'react';
import lockGray from '../../img/app/lock_gray.png';
import unlockGreen from '../../img/app/unlock_green.png';
import keyGray from '../../img/app/key_gray.png';
import keyGreen from '../../img/app/key_green.png';
import clockGray from '../../img/app/clock_gray.png';
import clockGreen from '../../img/app/clock_green.png';
import moreDark from '../../img/app/more_dark.png';
import homeGreen from '../../img/app/home_green.png';
import faqGray from '../../img/app/faq_gray.png';
import receiptGray from '../../img/app/receipt_gray.png';
import referralsGray from '../../img/app/referrals_gray.png';
import profileDark from '../../img/app/profile_dark.png';
import plusDark from '../../img/app/plus_dark.png';
import keySmallDark from '../../img/app/key_small_dark.png';
import deleteTrash from '../../img/app/delete_trash.png';
import appIcon from '../../img/app/app_icon.png';

export const C = {
  GREEN: '#41E1B0',
  MEDIUMGRAY: '#919496',
  LIGHTGRAY: '#F2F2F2',
  DARKGRAY: '#242a2d',
  DARKESTGRAY: '#0c0c0f',
  WHITE: '#f8f8f8',
  RED: '#f45532',
};

// Avenir Next ships with iOS and macOS, matching the app. Other platforms fall back to their system font.
const AV = "'Avenir Next', AvenirNext, Avenir, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const SF = "-apple-system, BlinkMacSystemFont, system-ui, 'Helvetica Neue', sans-serif";
const AVATAR_COLORS = ['#E57373', '#64B5F6', '#81C784', '#FFD54F', '#BA68C8', '#4DD0E1', '#FF8A65', '#A1887F'];
const ICONS = {
  lock_gray: lockGray,
  unlock_green: unlockGreen,
  key_gray: keyGray,
  key_green: keyGreen,
  clock_gray: clockGray,
  clock_green: clockGreen,
};

export const W = 393;
export const H = 852;
const TOP = 59;

// Style.js getAvatarColor: sum of char codes into the palette.
const avatarColor = (id) => AVATAR_COLORS[[...id].reduce((n, ch) => n + ch.charCodeAt(0), 0) % AVATAR_COLORS.length];

const T = ({ x, y, size, weight = 500, color = C.WHITE, family = AV, anchor, spacing, italic, children }) => (
  <text
    x={x}
    y={y}
    fill={color}
    fontFamily={family}
    fontSize={size}
    fontWeight={weight}
    fontStyle={italic ? 'italic' : undefined}
    textAnchor={anchor}
    letterSpacing={spacing}
  >
    {children}
  </text>
);

const Img = ({ src, x, y, w, h = w, opacity }) => (
  <image href={src} x={x} y={y} width={w} height={h} opacity={opacity} preserveAspectRatio="xMidYMid meet" />
);

const StatusBar = ({ time = '9:41' }) => (
  <g>
    <T x={64} y={34} size={17} weight={600} family={SF} anchor="middle">
      {time}
    </T>
    <rect x={134} y={11} width={125} height={37} rx={18.5} fill="#000" />
    <g transform="translate(292 24)" fill={C.WHITE}>
      <rect x="0" y="7.5" width="3" height="4.5" rx="0.8" />
      <rect x="4.8" y="5.5" width="3" height="6.5" rx="0.8" />
      <rect x="9.6" y="3" width="3" height="9" rx="0.8" />
      <rect x="14.4" y="0.5" width="3" height="11.5" rx="0.8" />
      <path d="M36.2 2.2c2.3 0 4.4.9 6 2.4l1.1-1.1a10 10 0 0 0-14.2 0l1.1 1.1a8.6 8.6 0 0 1 6-2.4z" />
      <path d="M36.2 5.6c1.4 0 2.6.5 3.6 1.4l1.1-1.1a6.6 6.6 0 0 0-9.4 0l1.1 1.1c1-.9 2.2-1.4 3.6-1.4z" />
      <path d="M36.2 9c.5 0 1 .2 1.3.5l-1.3 1.4-1.3-1.4c.3-.3.8-.5 1.3-.5z" />
      <rect x="50.5" y="0.5" width="24" height="12" rx="3.8" fill="none" stroke={C.WHITE} strokeOpacity="0.4" />
      <rect x="52.5" y="2.5" width="20" height="8" rx="2.2" />
      <path d="M76.2 4.6v4a2.2 2.2 0 0 0 0-4z" opacity="0.45" />
    </g>
  </g>
);

// FeedHeader: profile and plus 25pt from the edges, the key logo or the BuildingSwitcher button in the middle.
const FeedHeader = ({ building }) => {
  const bottom = TOP + 50;
  let middle = <Img src={keySmallDark} x={W / 2 - 24.5} y={bottom - 49} w={49} />;
  if (building) {
    const textW = building.length * 8.7;
    const left = W / 2 - (28 + 8 + textW + 16) / 2;
    middle = (
      <g>
        <circle cx={left + 14} cy={bottom - 18} r={14} fill={avatarColor(building)} />
        <T x={left + 14.5} y={bottom - 12.5} size={12.6} weight={700} anchor="middle">
          {building[0].toUpperCase()}
        </T>
        <T x={left + 36} y={bottom - 12.5} size={16} weight={600}>
          {building}
        </T>
        <T x={left + 36 + textW + 6} y={bottom - 14} size={10}>
          ▼
        </T>
      </g>
    );
  }

  return (
    <g>
      <Img src={profileDark} x={25} y={bottom - 40} w={40} />
      {middle}
      <Img src={plusDark} x={W - 65} y={bottom - 40} w={40} />
    </g>
  );
};

// RoutineAnimatedGrayButton. `card` = { title, icon, subtitle, active, percentage }.
const RoutineCard = ({ card, x, y, w, id }) => {
  const lines = card.subtitle.split('\n');
  const top = (70 - (27 + 16 * lines.length)) / 2;
  const on = card.active;
  return (
    <g transform={`translate(${x} ${y})`}>
      <clipPath id={`${id}c`}>
        <rect width={w} height={70} rx={15} />
      </clipPath>
      <clipPath id={`${id}t`}>
        <rect x={66} width={w - 66 - 57} height={70} />
      </clipPath>
      <g clipPath={`url(#${id}c)`}>
        <rect width={w} height={70} fill={C.DARKGRAY} opacity={on ? 0.5 : 0.6} />
        {on && <rect width={(w * (card.percentage ?? 100)) / 100} height={70} fill={C.DARKGRAY} />}
      </g>
      <Img src={ICONS[card.icon]} x={18} y={20} w={30} />
      <g clipPath={`url(#${id}t)`}>
        <T x={66} y={top + 20} size={20} weight={on ? 700 : 600} color={on ? C.WHITE : C.MEDIUMGRAY}>
          {card.title}
        </T>
        {lines.map((line, i) => (
          <T key={i} x={66} y={top + 39 + i * 16} size={12} weight={on ? 700 : 500} color={on ? C.GREEN : C.MEDIUMGRAY}>
            {line}
          </T>
        ))}
      </g>
      <Img src={moreDark} x={w - 47} y={20.5} w={29} opacity={on ? 0.35 : 1} />
    </g>
  );
};

const TABS = [
  ['Home', homeGreen],
  ['How To + FAQ', faqGray],
  ['Activity', receiptGray],
  ['Referrals', referralsGray],
];

const TabBar = () => (
  <g>
    <path d={`M0 ${H}V${H - 71}a20 20 0 0 1 20-20H${W - 20}a20 20 0 0 1 20 20V${H}z`} fill={C.DARKGRAY} />
    {TABS.map(([label, icon], i) => {
      const cx = (W / 4) * (i + 0.5);
      return (
        <g key={label}>
          <Img src={icon} x={cx - 12} y={H - 81} w={24} />
          <T x={cx} y={H - 40} size={12} anchor="middle" color={i === 0 ? C.GREEN : C.MEDIUMGRAY}>
            {label}
          </T>
        </g>
      );
    })}
    <rect x={W / 2 - 67} y={H - 13} width={134} height={5} rx={2.5} fill={C.WHITE} opacity={0.92} />
  </g>
);

// iOS banner for the buzz-in push from pushNotifications.js. `lines` is the body, already wrapped.
const Notification = ({ lines, time = 'now', y = 62, id }) => {
  const h = 34 + lines.length * 20;
  return (
    <g transform={`translate(16.5 ${y})`}>
      <filter id={`${id}s`} x="-20%" y="-40%" width="140%" height="180%">
        <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#000" floodOpacity="0.6" />
      </filter>
      <rect width={360} height={h} rx={24} fill="#202427" filter={`url(#${id}s)`} />
      <clipPath id={`${id}i`}>
        <rect x={14} y={(h - 38) / 2} width={38} height={38} rx={9} />
      </clipPath>
      <g clipPath={`url(#${id}i)`}>
        <Img src={appIcon} x={14} y={(h - 38) / 2} w={38} />
      </g>
      <T x={64} y={30} size={15} weight={600} family={SF}>
        Lowkey
      </T>
      <T x={344} y={30} size={13} weight={400} family={SF} color={C.MEDIUMGRAY} anchor="end">
        {time}
      </T>
      {lines.map((line, i) => (
        <T key={i} x={64} y={50 + i * 20} size={15} weight={400} family={SF}>
          {line}
        </T>
      ))}
    </g>
  );
};

// The website's "look here" marker: a glowing green ring around a box and an arrow pointing up at it.
const Callout = ({ x, y, w, h, rx, id }) => {
  const ax = x + w - 70;
  const top = y + h + 14;
  return (
    <g fill="none" stroke={C.GREEN} strokeLinecap="round" strokeLinejoin="round" filter={`url(#${id}g)`}>
      <filter id={`${id}g`} x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <rect x={x - 7} y={y - 7} width={w + 14} height={h + 14} rx={rx + 7} strokeWidth={4} />
      <path
        d={`M${ax} ${top + 80}V${top + 6}M${ax - 18} ${top + 24}L${ax} ${top + 4}L${ax + 18} ${top + 24}`}
        strokeWidth={8}
      />
    </g>
  );
};

// Home feed with routine cards, optionally a building name in the header and a notification banner.
// `callout` marks the lead card (what to tap) or the banner (the buzz-in push) like the website's old screenshots.
export const HomeScreen = ({ cards, building, notification, callout, uid }) => {
  const w = W * 0.89;
  const x = (W - w) / 2;
  return (
    <g>
      <StatusBar />
      <FeedHeader building={building} />
      {cards.map((card, i) => (
        <RoutineCard key={i} card={card} x={x} y={TOP + 65 + i * 80} w={w} id={`${uid}r${i}`} />
      ))}
      <TabBar />
      {notification && <Notification lines={notification} id={`${uid}n`} />}
      {callout &&
        (notification ? (
          <Callout x={16.5} y={62} w={360} h={34 + notification.length * 20} rx={24} id={`${uid}k`} />
        ) : (
          <Callout x={x} y={TOP + 65} w={w} h={70} rx={15} id={`${uid}k`} />
        ))}
    </g>
  );
};

const Avatar = ({ cx, cy, r, label, color }) => (
  <g>
    <circle cx={cx} cy={cy} r={r} fill={color ?? avatarColor(label)} />
    <T x={cx + r * 0.04} y={cy + r * 0.36} size={r * 0.9} weight={700} anchor="middle">
      {label[0].toUpperCase()}
    </T>
  </g>
);

const Badge = ({ x, y, label, bg, color, size = 12, pad = 8, h = 18, rx = 9 }) => {
  const w = label.length * size * 0.62 + pad * 2;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={rx} fill={bg} />
      <T x={x + w / 2} y={y + h / 2 + size * 0.36} size={size} weight={600} color={color} anchor="middle">
        {label}
      </T>
    </g>
  );
};

const UserRow = ({ user, y, remove }) => (
  <g>
    <Avatar cx={60} cy={y + 34} r={20} label={user.email} color={AVATAR_COLORS[user.color]} />
    <T x={92} y={y + 30} size={16} weight={600}>
      {user.email}
    </T>
    <T x={92} y={y + 50} size={14} weight={400} color={C.MEDIUMGRAY}>
      {user.phone}
    </T>
    {remove && (
      <Badge
        x={293}
        y={y + 22}
        label="Remove"
        bg="rgba(244,85,50,.12)"
        color={C.RED}
        size={13}
        pad={10}
        h={24}
        rx={12}
      />
    )}
  </g>
);

// ManageInvitedUsersScreen: the owner plus roommates who receive buzzer calls.
export const SharedAccessScreen = ({ users }) => {
  const [owner, ...others] = users;
  const listH = others.length * 68;
  return (
    <g>
      <StatusBar />
      <Img src={keySmallDark} x={W / 2 - 24.5} y={TOP} w={49} />
      <T x={24} y={161} size={28} weight={700}>
        Manage shared access
      </T>
      <T x={24} y={198} size={16} weight={400} color={C.MEDIUMGRAY}>
        See who has access to your buzzer and
      </T>
      <T x={24} y={222} size={16} weight={400} color={C.MEDIUMGRAY}>
        manage each person individually.
      </T>
      <T x={24} y={268} size={12} weight={600} color={C.MEDIUMGRAY} spacing={1}>
        YOU
      </T>
      <Badge x={62} y={255} label="Primary user" bg="rgba(65,225,176,.15)" color={C.GREEN} />
      <rect x={24} y={284} width={345} height={68} rx={16} fill={C.DARKGRAY} />
      <UserRow user={owner} y={284} />
      <T x={24} y={394} size={12} weight={600} color={C.MEDIUMGRAY} spacing={1}>
        USERS WITH ACCESS
      </T>
      <Badge x={166} y={381} label={String(others.length)} bg="rgba(65,225,176,.15)" color={C.GREEN} />
      <T x={24} y={427} size={14} weight={400} color={C.MEDIUMGRAY}>
        These users can receive buzzer calls and
      </T>
      <T x={24} y={448} size={14} weight={400} color={C.MEDIUMGRAY}>
        control your buzzer.
      </T>
      <rect x={24} y={468} width={345} height={listH} rx={16} fill={C.DARKGRAY} />
      {others.map((u, i) => (
        <g key={u.email}>
          {i > 0 && <rect x={40} y={468 + i * 68} width={313} height={0.5} fill="rgba(248,250,252,.15)" />}
          <UserRow user={u} y={468 + i * 68} remove />
        </g>
      ))}
      <rect x={24} y={468 + listH + 32} width={345} height={47} rx={10} fill={C.GREEN} />
      <T x={W / 2} y={468 + listH + 63} size={20} weight={600} color="#000" anchor="middle">
        Share invite code
      </T>
    </g>
  );
};

const BuildingRow = ({ b, y, current }) => {
  const nameW = b.name.length * 8.4;
  const badge = current ? { label: 'Active', bg: C.GREEN } : b.primary ? { label: 'Primary', bg: C.LIGHTGRAY } : null;
  return (
    <g>
      {current && <rect x={16} y={y} width={W - 32} height={68} rx={12} fill="rgba(45,204,112,.15)" stroke={C.GREEN} />}
      <Avatar cx={50} cy={y + 34} r={22} label={b.name} />
      <T x={84} y={y + 31} size={16}>
        {b.name}
      </T>
      {badge && (
        <Badge
          x={84 + nameW + 8}
          y={y + 16}
          label={badge.label}
          bg={badge.bg}
          color="#000"
          size={10}
          pad={6}
          h={18}
          rx={4}
        />
      )}
      <T x={84} y={y + 51} size={13} weight={400} color={C.MEDIUMGRAY}>
        {b.phone}
      </T>
      <T x={current ? 349 : 313} y={y + 40} size={16} color={C.MEDIUMGRAY} anchor="middle">
        ✎
      </T>
      {!current && <Img src={deleteTrash} x={340} y={y + 25} w={18} />}
    </g>
  );
};

// BuildingSwitcher's bottom sheet open over the home feed: current building, other buildings, and Add Another Building.
export const BuildingSwitcherScreen = ({ buildings, cards, uid }) => {
  const [current, ...others] = buildings;
  const sheetTop = H - 500;
  const listTop = sheetTop + 155;
  const footerTop = H - 96;
  return (
    <g>
      <HomeScreen cards={cards} building={current.name} uid={uid} />
      <rect width={W} height={H} fill="rgba(0,0,0,.6)" />
      <path d={`M0 ${H}V${sheetTop + 20}a20 20 0 0 1 20-20H${W - 20}a20 20 0 0 1 20 20V${H}z`} fill={C.DARKGRAY} />
      <rect x={W / 2 - 20} y={sheetTop + 10} width={40} height={5} rx={2.5} fill={C.MEDIUMGRAY} />
      <BuildingRow b={current} y={sheetTop + 33} current />
      <T x={16} y={sheetTop + 136} size={12} weight={600} color={C.MEDIUMGRAY} spacing={1}>
        OTHER BUILDINGS
      </T>
      {others.length >= 4 && (
        <g>
          <rect x={W - 136} y={sheetTop + 117} width={120} height={28} rx={8} fill="rgba(150,150,150,.15)" />
          <T x={W - 126} y={sheetTop + 136} size={15} color={C.MEDIUMGRAY}>
            ⌕
          </T>
          <T x={W - 108} y={sheetTop + 136} size={13} weight={400} color={C.MEDIUMGRAY}>
            Search...
          </T>
        </g>
      )}
      <rect x={16} y={listTop - 1} width={W - 32} height={1} fill="rgba(150,150,150,.2)" />
      <clipPath id={`${uid}l`}>
        <rect y={listTop} width={W} height={footerTop - listTop} />
      </clipPath>
      <g clipPath={`url(#${uid}l)`}>
        {others.map((b, i) => (
          <BuildingRow key={b.name} b={b} y={listTop + i * 68} />
        ))}
      </g>
      <rect x={16} y={footerTop} width={W - 32} height={1} fill="rgba(150,150,150,.2)" />
      <rect x={16} y={footerTop + 16} width={W - 32} height={50} rx={12} fill={C.GREEN} />
      <T x={W / 2} y={footerTop + 47} size={18} weight={600} color="#000" anchor="middle">
        + Add Another Building
      </T>
      <rect x={W / 2 - 67} y={H - 13} width={134} height={5} rx={2.5} fill={C.WHITE} opacity={0.92} />
    </g>
  );
};

// iPhone frame (titanium band, 12pt bezel) around a screen. Scales to its CSS box like any SVG.
const AppPhone = ({ label, className = '', style, children }) => {
  const uid = useId().replace(/:/g, '');
  const screen = typeof children === 'function' ? children(uid) : children;
  return (
    <svg
      viewBox={`-4 0 ${W + 32} ${H + 24}`}
      className={className}
      style={{ aspectRatio: `${W + 32} / ${H + 24}`, ...style }}
      role="img"
      aria-label={label}
    >
      <defs>
        <linearGradient id={`${uid}band`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#74777c" />
          <stop offset="0.25" stopColor="#2c2e31" />
          <stop offset="0.5" stopColor="#5a5d62" />
          <stop offset="0.8" stopColor="#2a2c2f" />
          <stop offset="1" stopColor="#62656a" />
        </linearGradient>
        <clipPath id={`${uid}screen`}>
          <rect width={W} height={H} rx={55} />
        </clipPath>
      </defs>
      {[
        [-3.5, 130, 32],
        [-3.5, 188, 62],
        [-3.5, 264, 62],
        [W + 22.5, 222, 96],
      ].map(([x, y, h]) => (
        <rect key={y} x={x} y={y} width={5} height={h} rx={2} fill="#3a3c40" />
      ))}
      <rect width={W + 24} height={H + 24} rx={67} fill={`url(#${uid}band)`} />
      <rect x={2.2} y={2.2} width={W + 19.6} height={H + 19.6} rx={65} fill="#050506" />
      <g transform="translate(12 12)" clipPath={`url(#${uid}screen)`}>
        <rect width={W} height={H} fill={C.DARKESTGRAY} />
        {screen}
      </g>
    </svg>
  );
};

export default AppPhone;
